import { ROLE_LEVEL, ROLE_PERMISSIONS, expandPermissions } from '@supernova/shared';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';

// `employee` is the stored value for Cashier, kept so existing users and POS
// PIN logins are unaffected.
const LINE_ROLE_OPTIONS = [
  { value: 'employee', label: 'Cashier' },
  { value: 'inventory_manager', label: 'Inventory Manager' },
];
export const ALL_ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  ...LINE_ROLE_OPTIONS,
];

/** Built-in roles, shown read-only on the Roles pages. */
export const BUILT_IN = ['owner', 'admin', 'manager', 'employee', 'inventory_manager'] as const;
export type BuiltInRole = (typeof BUILT_IN)[number];
export const BUILT_IN_SUMMARY: Record<BuiltInRole, string> = {
  owner: 'Everything',
  admin: 'Everything except owner-only settings',
  manager: 'Runs the store, no paying out',
  employee: 'Adds and edits the catalogue',
  inventory_manager: 'Catalogue, promotions and orders',
};

/**
 * Who may do what on the User Management pages. The same rules auth enforces:
 * owners and admins manage everyone; anyone else holding Users permissions
 * manages only staff below their own level.
 */
export function useUserAccess() {
  const { user } = useAuthStore();
  const permissions = usePermissions();
  const myLevel = ROLE_LEVEL[user?.role ?? ''] ?? 0;
  const manageAll = myLevel >= ROLE_LEVEL.admin;
  const canCreate = manageAll || permissions.has('users.create');
  const canEdit = manageAll || permissions.has('users.edit');
  const canDeactivate = manageAll || permissions.has('users.deactivate');
  // Only owners and admins may grant extra permissions (enforced in auth too).
  const canGrant = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'super_admin';
  const canManageRoles = canGrant && user?.role !== 'super_admin';

  // Below Admin, a user can be managed when they rank lower, or rank the same and
  // hold nothing the manager lacks (auth decides the same way).
  const canActOn = (row: any) => {
    if (manageAll) return true;
    if (row.id === user?.id) return false;
    const level = ROLE_LEVEL[row.role] ?? Infinity;
    if (level > myLevel) return false;
    if (level < myLevel) return true;
    if (row.customRoleId) return false;
    return expandPermissions([...(ROLE_PERMISSIONS[row.role] ?? []), ...(row.extraPermissions ?? [])]).every((k) =>
      permissions.has(k),
    );
  };
  // Anyone else may give a built-in role up to Manager and their own level, with no more access than they have.
  const roleOptions = manageAll
    ? ALL_ROLE_OPTIONS
    : ALL_ROLE_OPTIONS.filter(
        (o) =>
          o.value !== 'admin' &&
          ROLE_LEVEL[o.value] <= myLevel &&
          ROLE_PERMISSIONS[o.value].every((k) => permissions.has(k)),
      );

  return {
    user,
    canCreate,
    canEdit,
    canDeactivate,
    canGrant,
    canManageRoles,
    canActOn,
    roleOptions,
  };
}
